import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, Eye, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import { useLookups } from "@/hooks/useLookups";
import {
  useAddStudentMutation,
  useDeleteStudentMutation,
  useGetStudentsQuery,
  useUpdateStudentMutation,
  type Student,
} from "@/redux/slices/studentApi";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

const schema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(255),
  mobile: z.string().trim().min(7).max(20),
  yearId: z.string().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"]),
  supportKey: z.boolean().default(false),
  collegeName: z.string().optional(),
  state: z.string().optional(),
  referralCode: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export default function StudentsPage() {
  const navigate = useNavigate();
  const list = useListState();
  const { years, yearName } = useLookups();
  const { data, isFetching, refetch } = useGetStudentsQuery(list.queryParams());
  const [addStudent, addM] = useAddStudentMutation();
  const [updateStudent, updateM] = useUpdateStudentMutation();
  const [deleteStudent, deleteM] = useDeleteStudentMutation();

  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<Student | null>(null);
  const [editing, setEditing] = useState<Student | null>(null);
  const [toDelete, setToDelete] = useState<Student | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", mobile: "", yearId: "", status: "active", supportKey: false, collegeName: "", state: "", referralCode: "" },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ name: "", email: "", mobile: "", yearId: years[0]?.id ?? "", status: "active", supportKey: false, collegeName: "", state: "", referralCode: "" });
    setOpen(true);
  };
  const openEdit = (s: Student) => {
    setEditing(s);
    form.reset({ name: s.name, email: s.email, mobile: s.mobile, yearId: s.yearId, status: s.status, supportKey: s.supportKey || false, collegeName: s.collegeName || "", state: s.state || "", referralCode: s.referralCode || "" });
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateStudent({ id: editing.id, body: v }).unwrap();
        toast.success("Student updated");
      } else {
        await addStudent({ ...v, enrollmentDate: new Date().toISOString() }).unwrap();
        toast.success("Student added");
      }
      setOpen(false);
    } catch {
      toast.error("Failed");
    }
  };

  const columns: Column<Student>[] = [
    {
      key: "name",
      header: "Name",
      render: (r) => (
        <Link to={`/students/${r.id}`} className="font-bold text-foreground hover:text-primary transition-colors">
          {r.name}
        </Link>
      ),
    },
    { key: "email", header: "Email", render: (r) => <span className="text-muted-foreground">{r.email}</span> },
    { key: "mobile", header: "Mobile" },
    { key: "state", header: "State", render: (r) => r.state || "-" },
    { key: "yearId", header: "Course / Year", render: (r) => yearName(r.yearId) },
    { key: "createdAt", header: "Enrolled", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
    { key: "supportKey", header: "Support Key", render: (r) => r.supportKey ? <span className="text-primary font-semibold">Active</span> : <span className="text-muted-foreground">None</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Students"
        description="Manage enrolled Ayurveda students"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => (refetch as any)()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Add Student
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search name, email, mobile..."
            className="flex-1"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.yearId ?? "all"} onValueChange={(v) => list.setFilter("yearId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="All years" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={list.filters.status ?? "all"} onValueChange={(v) => list.setFilter("status", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={list.order} onValueChange={(v) => list.setSort("enrollmentDate", v as any)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Latest first</SelectItem>
                <SelectItem value="asc">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" onClick={() => navigate(`/students/${row.id}`)} title="View Student Profile">
                <Eye className="h-4 w-4 text-primary" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => openEdit(row)} title="Edit Student">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive" title="Delete Student">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg shadow-2xl border-none p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]">
          <DialogHeader className="p-8 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-2xl font-bold">{editing ? "Edit Student Profile" : "Register New Student"}</DialogTitle>
            <DialogDescription className="font-medium">Manage student access and personal information.</DialogDescription>
          </DialogHeader>
          <div className="px-8 py-4 overflow-y-auto flex-1">
            <form id="student-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Full name" error={form.formState.errors.name?.message}>
                <Input {...form.register("name")} className="h-11 shadow-sm" placeholder="e.g. Rahul Sharma" />
              </Field>
              <Field label="Email Address" error={form.formState.errors.email?.message}>
                <Input type="email" {...form.register("email")} className="h-11 shadow-sm" placeholder="rahul@example.com" />
              </Field>
              <Field label="Mobile Number" error={form.formState.errors.mobile?.message}>
                <Input {...form.register("mobile")} className="h-11 shadow-sm" placeholder="+91 XXXXX XXXXX" />
              </Field>
              <Field label="Academic year" error={form.formState.errors.yearId?.message}>
                <Select value={form.watch("yearId")} onValueChange={(v) => form.setValue("yearId", v)}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent>
                    {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="State" error={form.formState.errors.state?.message}>
                <Input {...form.register("state")} className="h-11 shadow-sm" placeholder="e.g. Maharashtra" />
              </Field>
              <Field label="College Name" error={form.formState.errors.collegeName?.message}>
                <Input {...form.register("collegeName")} className="h-11 shadow-sm" placeholder="e.g. Govt Ayurveda College" />
              </Field>
              <Field label="Referral Code" error={form.formState.errors.referralCode?.message}>
                <Input {...form.register("referralCode")} className="h-11 shadow-sm" placeholder="e.g. ALUMNI20" />
              </Field>
              <Field label="Account Status">
                <Select value={form.watch("status")} onValueChange={(v) => form.setValue("status", v as any)}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex flex-row items-center justify-between rounded-lg border p-4 col-span-1 sm:col-span-2">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold text-primary">Class Support Key</Label>
                  <p className="text-sm text-muted-foreground">Give this student free access to the full application.</p>
                </div>
                <Switch
                  checked={form.watch("supportKey")}
                  onCheckedChange={(checked) => form.setValue("supportKey", checked)}
                />
              </div>
            </form>
          </div>
          <DialogFooter className="border-t p-8 bg-muted/10 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-6 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="student-form" type="submit" disabled={addM.isLoading || updateM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20">
              {editing ? "Save Profile" : "Register Student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-md shadow-2xl border-none rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{viewing?.name}</DialogTitle>
            <DialogDescription className="font-medium text-primary">{viewing?.email}</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="mt-4 space-y-1 rounded-2xl border bg-muted/30 p-4">
              <Row label="Mobile" value={viewing.mobile} />
              <Row label="Course / Year" value={yearName(viewing.yearId)} />
              <Row label="Enrollment" value={format(new Date(viewing.createdAt), "dd MMM yyyy")} />
              <Row label="State" value={viewing.state || "-"} />
              <Row label="College Name" value={viewing.collegeName || "-"} />
              <Row label="Referral Code" value={viewing.referralCode || "-"} />
              <Row label="Support Key" value={viewing.supportKey ? "Active (Free Access)" : "None"} />
              <Row label="Status" value={<StatusBadge status={viewing.status} />} />
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={() => setViewing(null)} className="rounded-xl">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteStudent(toDelete.id).unwrap();
          toast.success("Deleted");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Delete student account?"
        description={`"${toDelete?.name}" will be permanently removed from the system. Access will be revoked immediately.`}
      />
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold">{label}</Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2.5 last:border-0 border-b border-border/50">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-tight">{label}</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}
